/**
 * Weather ambience. Three permanently-running beds — city, wind, rain — whose gains are
 * crossfaded by weather. Nothing is built or torn down on a weather change, so switching is
 * free and click-free.
 *
 *   city — pink noise under a 900 Hz lowpass with a very slow swell, plus a 58 Hz hum. That
 *          is what "distant traffic on Saint-Laurent, two blocks away" sounds like: no
 *          identifiable events, just a low grey mass that breathes.
 *   wind — white noise through a bandpass whose centre wanders 260–940 Hz on one LFO while a
 *          second, faster LFO gusts the level. Two incommensurate rates is the cheapest way
 *          to stop a wind bed sounding like a loop.
 *   rain — a bright hiss above 1.5 kHz plus a 700 Hz body, with a 3.1 Hz patter modulation
 *          so it has texture rather than being flat noise.
 */

import { AUDIO_TUNING as T } from './tuning.js';

const AM = T.ambience;
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

export class Ambience {
  constructor(graph) {
    this.graph = graph;
    this.built = false;
    this.weather = 'thaw';
    this.density = 1;
    /** Current blended weights [city, wind, rain]; smoothed toward the target. */
    this.mix = [1, 0.18, 0];
    this.enabled = true;
  }

  build() {
    const g = this.graph;
    if (!g.live || this.built) return;
    const a = g.actx;
    const now = a.currentTime;

    this.out = a.createGain();
    this.out.gain.value = 1;
    this.out.connect(g.bus.ambience);

    // ---------------- city
    this.cityGain = a.createGain(); this.cityGain.gain.value = 0.0001;
    this.cityGain.connect(this.out);
    this.citySrc = g.makeNoiseSource('pink');
    this.citySrc.playbackRate.value = 0.63;
    this.cityLp = a.createBiquadFilter();
    this.cityLp.type = 'lowpass';
    this.cityLp.frequency.value = AM.city.lpHz;
    this.cityLp.Q.value = AM.city.q;
    this.citySrc.connect(this.cityLp);
    this.cityBody = a.createGain(); this.cityBody.gain.value = AM.city.gain;
    this.cityLp.connect(this.cityBody); this.cityBody.connect(this.cityGain);
    // slow swell
    this.citySwell = a.createOscillator(); this.citySwell.type = 'sine';
    this.citySwell.frequency.value = AM.city.swellHz;
    this.citySwellDepth = a.createGain();
    this.citySwellDepth.gain.value = AM.city.swellDepth * AM.city.gain;
    this.citySwell.connect(this.citySwellDepth);
    this.citySwellDepth.connect(this.cityBody.gain);
    this.citySwell.start(now);
    // hum
    this.hum = a.createOscillator(); this.hum.type = 'sine';
    this.hum.frequency.value = AM.city.humHz;
    this.humGain = a.createGain(); this.humGain.gain.value = AM.city.humGain;
    this.hum.connect(this.humGain); this.humGain.connect(this.cityGain);
    this.hum.start(now);

    // ---------------- wind
    this.windGain = a.createGain(); this.windGain.gain.value = 0.0001;
    this.windGain.connect(this.out);
    this.windSrc = g.makeNoiseSource('white');
    this.windSrc.playbackRate.value = 0.71;
    this.windBp = a.createBiquadFilter();
    this.windBp.type = 'bandpass';
    this.windBp.frequency.value = (AM.wind.hzMin + AM.wind.hzMax) / 2;
    this.windBp.Q.value = AM.wind.q;
    this.windSrc.connect(this.windBp);
    this.windBody = a.createGain(); this.windBody.gain.value = AM.wind.gain;
    this.windBp.connect(this.windBody); this.windBody.connect(this.windGain);
    // wander LFO on the filter
    this.windWander = a.createOscillator(); this.windWander.type = 'sine';
    this.windWander.frequency.value = AM.wind.wanderHz;
    this.windWanderDepth = a.createGain();
    this.windWanderDepth.gain.value = (AM.wind.hzMax - AM.wind.hzMin) / 2;
    this.windWander.connect(this.windWanderDepth);
    this.windWanderDepth.connect(this.windBp.frequency);
    this.windWander.start(now);
    // gust LFO on level, deliberately not a multiple of the wander rate
    this.windGust = a.createOscillator(); this.windGust.type = 'sine';
    this.windGust.frequency.value = AM.wind.gustHz;
    this.windGustDepth = a.createGain();
    this.windGustDepth.gain.value = AM.wind.gustDepth * AM.wind.gain;
    this.windGust.connect(this.windGustDepth);
    this.windGustDepth.connect(this.windBody.gain);
    this.windGust.start(now);

    // ---------------- rain
    this.rainGain = a.createGain(); this.rainGain.gain.value = 0.0001;
    this.rainGain.connect(this.out);
    this.rainSrc = g.makeNoiseSource('white');
    this.rainSrc.playbackRate.value = 1.23;
    this.rainHp = a.createBiquadFilter();
    this.rainHp.type = 'highpass';
    this.rainHp.frequency.value = AM.rain.hpHz;
    this.rainSrc.connect(this.rainHp);
    this.rainBody = a.createGain(); this.rainBody.gain.value = AM.rain.gain;
    this.rainHp.connect(this.rainBody); this.rainBody.connect(this.rainGain);

    this.rainMidSrc = g.makeNoiseSource('pink');
    this.rainMidSrc.playbackRate.value = 1.09;
    this.rainMidBp = a.createBiquadFilter();
    this.rainMidBp.type = 'bandpass';
    this.rainMidBp.frequency.value = AM.rain.bodyHz;
    this.rainMidBp.Q.value = AM.rain.bodyQ;
    this.rainMidGain = a.createGain(); this.rainMidGain.gain.value = AM.rain.bodyGain;
    this.rainMidSrc.connect(this.rainMidBp); this.rainMidBp.connect(this.rainMidGain);
    this.rainMidGain.connect(this.rainGain);

    this.patter = a.createOscillator(); this.patter.type = 'sine';
    this.patter.frequency.value = AM.rain.patterHz;
    this.patterDepth = a.createGain();
    this.patterDepth.gain.value = AM.rain.patterDepth * AM.rain.gain;
    this.patter.connect(this.patterDepth);
    this.patterDepth.connect(this.rainBody.gain);
    this.patter.start(now);

    this.built = true;
  }

  set(weather, density) {
    if (weather && AM.weather[weather]) this.weather = weather;
    if (density != null) this.density = clamp01(density);
  }

  update(dt) {
    const target = AM.weather[this.weather] || AM.weather.thaw;
    const k = 1 - Math.exp(-dt / Math.max(0.05, AM.crossfade));
    for (let i = 0; i < 3; i++) this.mix[i] += (target[i] * this.density - this.mix[i]) * k;

    if (!this.graph.live || !this.built || !this.enabled) return;
    const g = this.graph;
    g.param(this.cityGain.gain, 0.0001 + this.mix[0], 0.2);
    g.param(this.windGain.gain, 0.0001 + this.mix[1], 0.2);
    g.param(this.rainGain.gain, 0.0001 + this.mix[2], 0.2);
  }

  setEnabled(on) {
    this.enabled = on;
    if (this.graph.live && this.built) this.graph.param(this.out.gain, on ? 1 : 0.0001, 0.05);
  }

  reset() { this.weather = 'thaw'; this.density = 1; this.mix[0] = 1; this.mix[1] = 0.18; this.mix[2] = 0; }

  stats() {
    return {
      weather: this.weather,
      density: +this.density.toFixed(2),
      mix: this.mix.map((v) => +v.toFixed(3)),
    };
  }
}
